import { useState, useEffect } from "react";
import { Bell, BellOff, Send, MessageCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AlertSettings() {
  const [config, setConfig] = useState({
    telegram_enabled: false,
    telegram_chat_ids: [],
    score_threshold: 4,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/v1/sentinel/alerts/config`);
        setConfig(res.data);
      } catch (err) {
        console.error('Failed to fetch alert config:', err);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await axios.put(`${BACKEND_URL}/api/v1/sentinel/alerts/config`, {
        score_threshold: config.score_threshold,
        telegram_enabled: config.telegram_enabled,
      });
      setConfig(prev => ({ ...prev, ...res.data }));
      setMessage('Saved');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Failed to save alert config:', err);
      setMessage('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTestAlert = async () => {
    setTesting(true);
    setMessage('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/v1/sentinel/alerts/test`);
      setMessage(`Test sent to ${res.data.sent} chat(s)`);
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Test failed';
      setMessage(detail);
      setTimeout(() => setMessage(''), 4000);
    } finally {
      setTesting(false);
    }
  };

  const registered = config.telegram_chat_ids?.length > 0;

  return (
    <div data-testid="alert-settings" className="space-y-3">
      <h3 className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">
        Telegram Alerts
      </h3>

      {/* Connection Status */}
      <div className="flex items-center gap-2 text-xs font-mono">
        <MessageCircle size={12} className={registered ? "text-emerald-400" : "text-zinc-600"} />
        <span className={registered ? "text-emerald-400" : "text-zinc-500"}>
          {registered ? `${config.telegram_chat_ids.length} chat(s) linked` : "No chats linked"}
        </span>
      </div>

      {!registered && (
        <div className="text-[10px] font-mono text-zinc-600 leading-relaxed bg-zinc-900/50 rounded p-2 border border-zinc-800">
          Search <span className="text-amber-400">@macroflare_alerts_bot</span> on Telegram and send <span className="text-amber-400">/start</span> to link your account.
        </div>
      )}

      {/* Enable/Disable Toggle */}
      <button
        data-testid="alert-toggle-btn"
        onClick={() => setConfig(prev => ({ ...prev, telegram_enabled: !prev.telegram_enabled }))}
        className={`w-full flex items-center justify-between px-3 py-2 rounded text-xs font-mono border transition-all ${
          config.telegram_enabled
            ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
            : "bg-zinc-900/50 border-zinc-800 text-zinc-500"
        }`}
      >
        <span className="flex items-center gap-2">
          {config.telegram_enabled ? <Bell size={12} /> : <BellOff size={12} />}
          {config.telegram_enabled ? "Alerts ON" : "Alerts OFF"}
        </span>
        <div className={`w-7 h-4 rounded-full relative transition-all ${
          config.telegram_enabled ? "bg-emerald-500" : "bg-zinc-700"
        }`}>
          <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${
            config.telegram_enabled ? "left-3.5" : "left-0.5"
          }`} />
        </div>
      </button>

      {/* Score Threshold */}
      <div className="space-y-1.5">
        <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-600">
          Score Threshold: ±{config.score_threshold}
        </label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map(val => (
            <button
              key={val}
              data-testid={`threshold-btn-${val}`}
              onClick={() => setConfig(prev => ({ ...prev, score_threshold: val }))}
              className={`flex-1 py-1.5 rounded text-xs font-mono font-bold transition-all ${
                config.score_threshold === val
                  ? val >= 4
                    ? "bg-red-500/20 border border-red-500/40 text-red-400"
                    : val >= 2
                    ? "bg-amber-500/20 border border-amber-500/40 text-amber-400"
                    : "bg-zinc-700 border border-zinc-600 text-zinc-300"
                  : "bg-zinc-900/50 border border-zinc-800 text-zinc-600 hover:text-zinc-400"
              }`}
            >
              ±{val}
            </button>
          ))}
        </div>
        <p className="text-[10px] font-mono text-zinc-600">
          {config.score_threshold >= 4
            ? "Only extreme moves"
            : config.score_threshold >= 2
            ? "Moderate + extreme moves"
            : "All scored tweets"}
        </p>
      </div>

      {/* Save + Test Buttons */}
      <div className="flex gap-2">
        <button
          data-testid="alert-save-btn"
          onClick={handleSave}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-mono bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700 transition-all disabled:opacity-50"
        >
          {saving ? "..." : "Save"}
        </button>
        <button
          data-testid="alert-test-btn"
          onClick={handleTestAlert}
          disabled={testing || !registered}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-mono bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 transition-all disabled:opacity-50"
        >
          <Send size={10} />
          {testing ? "..." : "Test"}
        </button>
      </div>

      {message && (
        <p data-testid="alert-message" className={`text-[10px] font-mono ${
          message.includes('fail') ? 'text-red-400' : 'text-emerald-400'
        }`}>
          {message}
        </p>
      )}
    </div>
  );
}